import AlertSystemModal from "@app/components/alert-system-modal";
import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import AlertSystemService from "@app/services/http/alert-system.service";
import { openPortalDialog } from "@app/services/modal.service";
import { AlertSystem, CreateAlertSystem, Meta } from "@app/types";
import { formatDateTime } from "@app/utils/util";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function CreatedDateColumnTable({ data }: { data: AlertSystem }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "10%",
    tdClassName: "truncate min-w-[150px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Thông báo",
    dataKey: "message",
    width: "70%",
    hasOrder: true,
  },
  {
    label: "Ngày tạo",
    dataKey: "createdDate",
    width: "20%",
    component: CreatedDateColumnTable,
    hasOrder: true,
  },
];

function AlertSystemManagement() {
  const [alertSystems, setAlertSystems] = useState<AlertSystem[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    AlertSystemService.getList(requestParams, abortController.signal).then(
      (res) => {
        setAlertSystems(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const modalObs = openPortalDialog(AlertSystemModal);

    modalObs.afterClosed().subscribe((data) => {
      if (data?.alertSystem) {
        const alertSystem: CreateAlertSystem = {
          message: data.alertSystem.message,
        };

        AlertSystemService.create(alertSystem).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (alertSystem: AlertSystem) => {
    const modalObs = openPortalDialog(AlertSystemModal, {
      isEdit: true,
      alertSystem,
    });

    modalObs.afterClosed().subscribe((data) => {
      if (data?.alertSystem) {
        const createAlertSystem: CreateAlertSystem = {
          message: data.alertSystem.message,
        };

        AlertSystemService.update(alertSystem.id, createAlertSystem).then(
          () => {
            addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
            forceUpdate();
          }
        );
      }
    });
  };

  const handleDeleteClick = (alertSystemId: number) => {
    if (alertSystemId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_NOTIFICATION_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          AlertSystemService.delete(alertSystemId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách thông báo</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách thông báo"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={alertSystems}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default AlertSystemManagement;
